import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { ownResourceKey } from '../../../ownKey'
import { decodeSnapshot, type OwnSaleSnapshot } from './snapshot'

/**
 * Снимки строк чека по источникам (срок `session`: переживает F5). Один источник — не больше одной начатой операции: пока снимок
 * не снят, новый не пишется поверх (повтор берёт прежний). Снимает его итог операции или «новая продажа».
 */
export interface OwnSaleSnapshotsState {
  byResource: Record<string, OwnSaleSnapshot>
}

const initialState: OwnSaleSnapshotsState = { byResource: {} }
export const snapshotKey = ownResourceKey

const slice = createSlice({
  name: 'own/saleSnapshots',
  initialState,
  reducers: {
    ownSaleSnapshotStarted(state, action: PayloadAction<{ resourceId: string; snapshot: OwnSaleSnapshot }>) {
      const key = snapshotKey(action.payload.resourceId)
      // Новая операция не пишется поверх начатой. Возврат нового состояния (а не правка черновика): снимок целиком неизменяем.
      return key in state.byResource ? state : { byResource: { ...state.byResource, [key]: action.payload.snapshot } }
    },
    ownSaleSnapshotCleared(state, action: PayloadAction<{ resourceId: string }>) {
      delete state.byResource[snapshotKey(action.payload.resourceId)]
    },
    ownSaleSnapshotsRestored(_state, action: PayloadAction<OwnSaleSnapshotsState['byResource']>) {
      return { byResource: action.payload }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      delete state.byResource[snapshotKey(action.payload.resourceId)]
    })
  },
})

export const { ownSaleSnapshotStarted, ownSaleSnapshotCleared, ownSaleSnapshotsRestored } = slice.actions
export const ownSaleSnapshotsReducer = slice.reducer

/** Не больше стольких источников в записи: больше на одном устройстве не бывает, лишнее — мусор. */
const MAX_SNAPSHOTS = 20

export function decodeSnapshots(raw: unknown): OwnSaleSnapshotsState['byResource'] | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined
  const result: OwnSaleSnapshotsState['byResource'] = {}
  for (const [key, value] of Object.entries(raw).slice(0, MAX_SNAPSHOTS)) {
    const snapshot = decodeSnapshot(value)
    // Одна негодная запись — вся запись негодна: частично восстановленный снимок хуже, чем никакой.
    if (snapshot === undefined) return undefined
    result[snapshotKey(key)] = snapshot
  }
  return result
}

export const snapshotOf = (state: OwnSaleSnapshotsState, resourceId: string): OwnSaleSnapshot | null => state.byResource[snapshotKey(resourceId)] ?? null
